import type { Exercise, Meal, Symptom } from './types';
import { getMealGroups, getMealIngredients } from './ingredientTaxonomy';
import { buildFlareContext, computeItemStat } from './insightStats';

export interface TimelineOptions {
  nowMs: number;
  fullDays?: number;
  maxChars?: number;
}

export interface EncodedTimeline {
  timeline: string;
  olderSummary: string;
  fullFromDate: string;
}

const DAY_MS = 86_400_000;
const NOTE_MAX = 80;
/** Most-eaten ingredients listed in the older-history summary. */
export const OLDER_SUMMARY_MAX_LINES = 60;

const oneLine = (t: string) => t.replace(/\s+/g, ' ').trim();

const pad = (n: number) => String(n).padStart(2, '0');

function localDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localStamp(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function mealLine(meal: Meal, ms: number): string {
  const titles = (meal.dishes ?? []).map((d) => oneLine(d.title ?? '')).filter(Boolean);
  const label = titles.length ? ` "${titles.join(' + ')}"` : '';
  const ings = getMealIngredients(meal)
    .map((i) => (i.grams === null ? i.name : `${i.name} ${Math.round(i.grams)}g`))
    .join('; ');
  const groups = [...getMealGroups(meal)].sort();
  const bracket = groups.length ? ` [${groups.join(', ')}]` : '';
  return `${localStamp(ms)} M ${meal.meal_type.charAt(0).toUpperCase()}${label}: ${ings}${bracket}`;
}

function symptomLine(s: Symptom, ms: number): string {
  const parts: string[] = [];
  const scores = s.symptom_scores ? Object.entries(s.symptom_scores).filter(([, v]) => v > 0) : [];
  if (s.symptom_scores && Object.keys(s.symptom_scores).length > 0) {
    for (const [id, v] of scores) parts.push(`${id}=${v}`);
  } else {
    if (s.bloating_score > 0) parts.push(`bloating=${s.bloating_score}`);
    if (s.soreness_score > 0) parts.push(`stomach_pain=${s.soreness_score}`);
  }
  if (parts.length === 0) parts.push('clear');
  if (s.stool_type !== undefined && s.stool_type !== null) parts.push(`B${s.stool_type}`);
  if (s.overall_wellbeing !== undefined && s.overall_wellbeing !== null) parts.push(`wb${s.overall_wellbeing}`);
  const notes = oneLine(s.notes ?? '').slice(0, NOTE_MAX).trim().replace(/"/g, "'");
  if (notes) parts.push(`"${notes}"`);
  return `${localStamp(ms)} S: ${parts.join(' ')}`;
}

function exerciseLine(e: Exercise, ms: number): string {
  let body = [e.activity_type, e.duration_minutes ? `${e.duration_minutes}m` : '', e.timing_relative_to_meal ?? '']
    .filter(Boolean)
    .join(' ');
  if (e.digestive_effect) body += `, ${e.digestive_effect}`;
  return `${localStamp(ms)} X: ${body}`;
}

export function encodeTimeline(
  meals: Meal[],
  symptoms: Symptom[],
  exercises: Exercise[],
  opts: TimelineOptions,
): EncodedTimeline {
  const maxChars = opts.maxChars ?? 60_000;
  const events = [
    ...meals.map((m) => ({ ms: new Date(m.timestamp_iso).getTime(), line: (ms: number) => mealLine(m, ms) })),
    ...symptoms.map((s) => ({ ms: new Date(s.timestamp_iso).getTime(), line: (ms: number) => symptomLine(s, ms) })),
    ...exercises.map((e) => ({ ms: new Date(e.timestamp_iso).getTime(), line: (ms: number) => exerciseLine(e, ms) })),
  ]
    .filter((e) => !Number.isNaN(e.ms))
    .sort((a, b) => a.ms - b.ms)
    .map((e) => ({ ms: e.ms, date: localDate(e.ms), text: e.line(e.ms) }));

  let days = opts.fullDays ?? 90;
  let fullFromDate = localDate(opts.nowMs - days * DAY_MS);
  let lines: string[] = [];
  for (;;) {
    fullFromDate = localDate(opts.nowMs - days * DAY_MS);
    lines = events.filter((e) => e.date >= fullFromDate).map((e) => e.text);
    if (lines.join('\n').length <= maxChars || days <= 7) break;
    days = Math.max(7, days - 7);
  }
  let total = lines.join('\n').length;
  while (lines.length > 0 && total > maxChars) {
    total -= lines[0].length + 1;
    lines.shift();
  }

  const olderMeals = meals.filter((m) => localDate(new Date(m.timestamp_iso).getTime()) < fullFromDate);
  let olderSummary = '';
  if (olderMeals.length > 0) {
    const byIngredient = new Map<string, Meal[]>();
    for (const m of olderMeals) {
      for (const ing of getMealIngredients(m)) {
        const list = byIngredient.get(ing.name) ?? [];
        list.push(m);
        byIngredient.set(ing.name, list);
      }
    }
    const ctx = buildFlareContext(meals, symptoms);
    olderSummary = [...byIngredient.entries()]
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .slice(0, OLDER_SUMMARY_MAX_LINES)
      .map(([name, ms]) => {
        const st = computeItemStat(name, ms, ctx);
        return `${name}: eaten ${st.timesEaten}, flared ${st.timesFollowedByFlare}, lift ${st.lift}`;
      })
      .join('\n');
  }

  return { timeline: lines.join('\n'), olderSummary, fullFromDate };
}
